import type { ProspectPracticeArea, TemplateType } from '@common/enums';
import type { QuoteTemplateEntry } from '@common/types/quoteTemplates';
import type { UserEntry } from '@common/types/users';
import { getMatterTypeLabel, getPracticeAreaLabel } from '@common/util';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import SearchIcon from '@mui/icons-material/Search';
import {
    Autocomplete,
    Box,
    Button,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    Link,
    Menu,
    MenuItem,
    Select,
    Stack,
    TextField,
    useTheme,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AlertDialog from '@/components/AlertDialog';
import DataTable, { type Column } from '@/components/DataTable';
import MatterTypeIcon from '@/components/MatterTypeIcon';
import SaveAsDefaultDialog from '@/components/SaveAsDefaultDialog';
import { useLawFirmContext } from '@/hooks/useLawFirmContext';
import { getIntakeTemplateForms } from '@/services/TemplateFormsService';
import { convertUTCDateToEST } from '@/utils';
import { errorSnackbar, successSnackbar } from '@/utils/snackbar';
import ConfigureApsTagsDialog from './ConfigureApsTagsDialog';
import type { FilterConfig } from './types';

interface Props {
    templates: QuoteTemplateEntry[];
    allTemplates?: QuoteTemplateEntry[];
    isLoading: boolean;
    filterConfig: FilterConfig;
    setFilterConfig(config: FilterConfig): void;
    handleDeleteClick(quoteTemplateId: number): void;
    templateType: TemplateType;
    users: UserEntry[];
    isDeleteLoading: boolean;
}

const TEMPLATE_CONFIG = {
    QuoteIntake: {
        editPath: (id: number) => `/quote/${id}`,
        createPath: '/quote-intake/setup',
        templateFormType: 'QuoteForm' as const,
        linkPrefix: '/q/',
    },
    IntakeOnly: {
        editPath: (id: number) => `/intake-only/setup/matter-type/${id}`,
        createPath: '/intake-only/setup/matter-type',
        templateFormType: 'IntakeForm' as const,
        linkPrefix: '/i/',
    },
};

const isApsMappingMatter = (template: QuoteTemplateEntry) =>
    template.process === 'Purchase' || template.process === 'Sale';

const hasIntakeForm = (template: QuoteTemplateEntry) =>
    template.templateForms.some((form) => form.templateFormType === 'IntakeForm');

const canConfigureApsTags = (template: QuoteTemplateEntry) =>
    isApsMappingMatter(template) &&
    (template.intakeFormAdded === 'Reference' || hasIntakeForm(template));

const fetchDefaultIntakeDesign = async (quoteTemplateId: number) => {
    const { data } = await getIntakeTemplateForms(quoteTemplateId);
    return {
        intakeFormDescription: data.headerDescription ?? '',
        textAfterSubmitIntakeForClient: data.footerTemplate ?? '',
        templateFormFields: data.templateFormFields,
        formGroups: data.formGroups ?? undefined,
        metadata: data.metadata,
    };
};

export default function TemplatesList({
    templates,
    allTemplates,
    isLoading,
    filterConfig,
    setFilterConfig,
    handleDeleteClick,
    templateType,
    users,
    isDeleteLoading,
}: Props) {
    const theme = useTheme();
    const navigate = useNavigate();
    const { isSuperAdmin } = useLawFirmContext();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [selectedQuoteTemplateId, setSelectedQuoteTemplateId] = useState<number | null>(null);
    const [actionsMenu, setActionsMenu] = useState<{
        anchorEl: HTMLElement;
        template: QuoteTemplateEntry;
    } | null>(null);
    const [configureTagsTemplate, setConfigureTagsTemplate] = useState<QuoteTemplateEntry | null>(
        null,
    );
    const [saveAsDefaultTemplate, setSaveAsDefaultTemplate] = useState<QuoteTemplateEntry | null>(
        null,
    );

    const config = TEMPLATE_CONFIG[templateType];

    const matterTypeFilterOptions = useMemo(() => {
        const sourceTemplates = allTemplates ?? templates;
        const matterTypes = [
            ...new Set(sourceTemplates.map((template) => template.process).filter(Boolean)),
        ];

        return matterTypes.sort((a, b) =>
            getMatterTypeLabel(a).localeCompare(getMatterTypeLabel(b)),
        );
    }, [allTemplates, templates]);

    const handleCopyLink = (form: QuoteTemplateEntry) => {
        const templateArray = form.templateForms;
        const formObj = templateArray?.find(
            (element) => element && element.templateFormType === config.templateFormType,
        );
        const templateCode = formObj?.templateFormCode;

        if (templateCode) {
            navigator.clipboard
                .writeText(`${window.location.origin}${config.linkPrefix}${templateCode}`)
                .then(() => successSnackbar('Link copied'))
                .catch((error) => errorSnackbar(error?.response?.data || 'Could not copy link'));
        } else {
            errorSnackbar('Could not copy link');
        }
    };

    const getLinkStatus = (form: QuoteTemplateEntry) => {
        const templateArray = form.templateForms;
        return templateArray?.find(
            (element) => element && element.templateFormType === config.templateFormType,
        );
    };

    const intakeCreationTypeColumn: Column<QuoteTemplateEntry> = {
        id: 'intakeFormAdded',
        label: 'Intake Creation Type',
        _render: (form) => {
            if (form.intakeFormAdded === 'Reference') {
                return (
                    <Link
                        sx={{
                            fontWeight: '500',
                            cursor: 'pointer',
                            textDecoration: 'none',
                        }}
                        onClick={() =>
                            navigate(
                                `/intake-only/setup/matter-type/${form.referencedIntakeOnlyQuoteTemplateId}`,
                            )
                        }
                    >
                        Referenced
                    </Link>
                );
            }
            return form.intakeFormAdded === 'Manually'
                ? 'Custom'
                : form.intakeFormAdded === 'Copy'
                  ? 'Copied'
                  : form.intakeFormAdded;
        },
        _isSortable: true,
    };

    const practiceAreaColumn: Column<QuoteTemplateEntry> = {
        id: 'practiceArea',
        label: 'Practice Area',
        _render: (form) => getPracticeAreaLabel(form.practiceArea),
        _isSortable: true,
    };

    const deleteColumn: Column<QuoteTemplateEntry> = {
        id: 'delete',
        label: 'Delete',
        _render: (form) => (
            <IconButton
                onClick={() => {
                    setSelectedQuoteTemplateId(form.quoteTemplateId);
                    setIsDeleteDialogOpen(true);
                }}
            >
                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
            </IconButton>
        ),
        _isSortable: false,
        _width: '40',
    };

    const apsTagsColumn: Column<QuoteTemplateEntry> = {
        id: 'apsPrefillMappingCount',
        label: 'APS Mappings',
        _render: (form) =>
            isApsMappingMatter(form) ? Number(form.apsPrefillMappingCount ?? 0) : '-',
        _isSortable: false,
    };

    const actionsColumn: Column<QuoteTemplateEntry> = {
        id: 'actions',
        label: 'Actions',
        _render: (form) => (
            <IconButton
                aria-label="Template actions"
                onClick={(event) => {
                    event.stopPropagation();
                    setActionsMenu({ anchorEl: event.currentTarget, template: form });
                }}
            >
                <MoreHorizIcon />
            </IconButton>
        ),
        _isSortable: false,
        _width: '72',
    };

    const isIntakeOnly = templateType === 'IntakeOnly';

    const columns: Column<QuoteTemplateEntry>[] = [
        {
            id: 'matterType',
            label: '',
            _render: (form) => (
                <MatterTypeIcon matterType={form.process} color="#7B7D80" width={28} />
            ),
            _isSortable: false,
            _width: '40',
        },
        {
            id: 'name',
            label: 'Name',
            _render: (form) => (
                <Link
                    sx={{
                        fontWeight: '500',
                        cursor: 'pointer',
                        textDecoration: 'none',
                    }}
                    onClick={() => navigate(config.editPath(form.quoteTemplateId))}
                >
                    {form.quoteTemplateName}
                </Link>
            ),
            _isSortable: false,
        },
        {
            id: 'process',
            label: 'Matter Type',
            _render: (form) => getMatterTypeLabel(form.process),
            _isSortable: true,
        },
        templateType === 'QuoteIntake' ? intakeCreationTypeColumn : practiceAreaColumn,
        {
            id: 'quoteApproverId',
            label: 'Owner',
            _render: (form) =>
                users?.find((user) => user.userId === form.quoteApproverId)?.fullName ?? '',
            _isSortable: true,
        },
        {
            id: 'link',
            label: 'Link',
            _render: (form) => {
                const formObj = getLinkStatus(form);
                return (
                    <IconButton
                        sx={{
                            minWidth: '24px',
                            height: 'fit-content !important',
                            padding: '0px',
                            color:
                                formObj?.status !== 'Draft'
                                    ? '#3678D7'
                                    : theme.palette.neutral.N_200,
                        }}
                        onClick={() => handleCopyLink(form)}
                        disableRipple={templateType === 'QuoteIntake'}
                        disabled={formObj?.status === 'Draft'}
                    >
                        <ContentCopyOutlinedIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                );
            },
            _isSortable: false,
        },
        {
            id: 'createdAt',
            label: 'Creation date',
            _render: (form) => convertUTCDateToEST(form.createdAt, 'MMMM d, yyyy'),
            _isSortable: true,
        },
        ...(isIntakeOnly && isSuperAdmin ? [apsTagsColumn] : []),
        isIntakeOnly ? actionsColumn : deleteColumn,
    ];

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleChangePage = (_event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleCancelDelete = () => {
        setSelectedQuoteTemplateId(null);
        setIsDeleteDialogOpen(false);
    };

    const handleConfirmDelete = () => {
        if (selectedQuoteTemplateId !== null) {
            handleDeleteClick(selectedQuoteTemplateId);
            setSelectedQuoteTemplateId(null);
        }
        setIsDeleteDialogOpen(false);
    };

    const handleFilterConfigChange = (
        ev: { target: { value: string | number } },
        keyName: string,
    ) => {
        const {
            target: { value },
        } = ev;
        setFilterConfig({
            ...filterConfig,
            [keyName]: value,
        });
    };

    const selectedOwner = () => {
        if (filterConfig.owner !== 0) {
            const selectedUser = users.find((user) => user.userId === filterConfig.owner);
            return {
                label: selectedUser?.fullName,
                value: selectedUser?.userId,
                key: selectedUser?.userId,
            };
        }
        return undefined;
    };

    return (
        <Stack spacing={2} mt={2}>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <Stack direction="row" spacing={1}>
                    <TextField
                        sx={{
                            backgroundColor: theme.palette.neutral.N_50,
                            height: '40px',
                            borderRadius: '8px',
                            color: theme.palette.neutral.gray,
                            width: '400px',
                        }}
                        placeholder="Search templates by name"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ fontSize: 14 }} />
                                </InputAdornment>
                            ),
                        }}
                        value={filterConfig.searchKey}
                        onChange={(ev) => handleFilterConfigChange(ev, 'searchKey')}
                    />
                    <FormControl
                        sx={{
                            backgroundColor: theme.palette.neutral.N_50,
                            height: '40px',
                            borderRadius: '8px',
                            color: theme.palette.neutral.gray,
                            width: '180px',
                        }}
                    >
                        <InputLabel id="matter-type-label">Matter type</InputLabel>
                        <Select
                            labelId="matter-type-label"
                            id="matter-type"
                            label="Matter type"
                            value={filterConfig.matterType}
                            onChange={(ev) => handleFilterConfigChange(ev, 'matterType')}
                        >
                            <MenuItem key="" value="">
                                All
                            </MenuItem>
                            {matterTypeFilterOptions.map((option) => (
                                <MenuItem key={option} value={option}>
                                    {getMatterTypeLabel(option)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl
                        sx={{
                            backgroundColor: theme.palette.neutral.N_50,
                            height: '40px',
                            borderRadius: '8px',
                            color: theme.palette.neutral.gray,
                            minWidth: '180px',
                        }}
                    >
                        <Autocomplete
                            value={selectedOwner()}
                            onChange={(_ev, values) => {
                                const val = values?.value ? values?.value : 0;
                                handleFilterConfigChange({ target: { value: val } }, 'owner');
                            }}
                            fullWidth
                            disablePortal
                            options={users.map((user) => ({
                                label: user.fullName,
                                value: user.userId,
                                key: user.userId,
                            }))}
                            renderInput={(params) => <TextField {...params} label="Owner" />}
                        />
                    </FormControl>
                </Stack>
                <Button onClick={() => navigate(config.createPath)} variant="contained">
                    CREATE TEMPLATE
                </Button>
            </Box>
            <DataTable
                generateKeyFrom="quoteTemplateId"
                isSelectable={false}
                isLoading={isLoading}
                columns={columns}
                rows={templates}
                page={page}
                rowsPerPage={rowsPerPage}
                handleChangePage={handleChangePage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
            />
            {isIntakeOnly && (
                <Menu
                    anchorEl={actionsMenu?.anchorEl}
                    open={Boolean(actionsMenu)}
                    onClose={() => setActionsMenu(null)}
                >
                    {isSuperAdmin && actionsMenu && canConfigureApsTags(actionsMenu.template) && (
                        <MenuItem
                            onClick={() => {
                                if (!actionsMenu) return;
                                setConfigureTagsTemplate(actionsMenu.template);
                                setActionsMenu(null);
                            }}
                        >
                            Configure Tags
                        </MenuItem>
                    )}
                    {isSuperAdmin && actionsMenu && hasIntakeForm(actionsMenu.template) && (
                        <MenuItem
                            onClick={() => {
                                if (!actionsMenu) return;
                                setSaveAsDefaultTemplate(actionsMenu.template);
                                setActionsMenu(null);
                            }}
                        >
                            Save as Default
                        </MenuItem>
                    )}
                    <MenuItem
                        onClick={() => {
                            if (!actionsMenu) return;
                            setSelectedQuoteTemplateId(actionsMenu.template.quoteTemplateId);
                            setIsDeleteDialogOpen(true);
                            setActionsMenu(null);
                        }}
                        sx={{ color: 'error.main' }}
                    >
                        Delete
                    </MenuItem>
                </Menu>
            )}
            <AlertDialog
                open={isDeleteDialogOpen}
                isLoading={isDeleteLoading}
                handleClose={handleCancelDelete}
                title="Are you sure you want to delete the template?"
                message="By clicking Accept you are deleting the selected template. You will need to contact Lexo Support if you want to recover it."
                handleConfirm={handleConfirmDelete}
            />
            {configureTagsTemplate && (
                <ConfigureApsTagsDialog
                    template={configureTagsTemplate}
                    onClose={() => setConfigureTagsTemplate(null)}
                />
            )}
            {saveAsDefaultTemplate && (
                <SaveAsDefaultDialog
                    practiceArea={saveAsDefaultTemplate.practiceArea as ProspectPracticeArea}
                    matterType={saveAsDefaultTemplate.process}
                    templateType="IntakeOnly"
                    defaultLawFirmId={saveAsDefaultTemplate.lawFirmId}
                    getIntakeDesign={() =>
                        fetchDefaultIntakeDesign(saveAsDefaultTemplate.quoteTemplateId)
                    }
                    onClose={() => setSaveAsDefaultTemplate(null)}
                />
            )}
        </Stack>
    );
}
